import { ExerciseIndex, RoutineIndex, UserIndex } from "@/utils/Algolia";
import _map from 'lodash/map'
import _merge from 'lodash/merge'
import _flatten from 'lodash/flatten'
import _uniq from 'lodash/uniq'
import _compact from 'lodash/compact'

import i18n from "@/plugins/i18n";

import { randomString } from "@/utils/Common";
import { db, Timestamp } from "@/utils/Firebase";
import moment from 'moment';

export const populateAssignedRoutines = async (assignedRoutines = [], mergeBlocks = false, populateCreators = false) => {

    // console.log('populating routines', assignedRoutines)
    let allIds = _compact(_uniq(_map(assignedRoutines, 'routineId')))
    if (allIds.length === 0) return []

    const algoliaResult = await RoutineIndex.search('', {
        filters: `objectID:${allIds.join(' OR objectID:')}`,
        hitsPerPage: 10000,
    })

    let foundRoutines = algoliaResult.hits

    if (mergeBlocks) {
        for (let routine of foundRoutines) {
            routine.blocks = await getMergedBlocks(routine.blocks)
        }
    }

    if (populateCreators) {
        assignedRoutines = await populateRoutinesCreators(assignedRoutines)
    }

    let allRoutines = []
    assignedRoutines.forEach(routine => {
        let foundRoutine = foundRoutines.find(rt => rt.objectID === routine.routineId)
        if (foundRoutine) {
            if (routine.name) delete foundRoutine.name
            allRoutines.push(_merge(routine, foundRoutine))
        } else {
            allRoutines.push(routine)
        }
    })

    return allRoutines

}

export const populateRoutinesCreators = async (routines) => {

    let coachesIDs = _compact(_uniq(_map(routines, 'coachId')))
    if (coachesIDs.length === 0) return routines

    const algoliaResult = await UserIndex.search('', {
        filters: `objectID:${coachesIDs.join(' OR objectID:')}`,
        hitsPerPage: 10000,
    })

    let foundUsers = algoliaResult.hits
    // console.log('found users', foundUsers)
    let allRoutines = []

    routines.forEach(routine => {
        let foundUser = foundUsers.find(usr => usr.objectID === routine.coachId)
        if (foundUser) {
            routine.createdBy = foundUser
        }
        allRoutines.push(routine)
    })

    return allRoutines

}


export const getExercisesByBlocks = async (blocks) => {

    let allIds = _uniq(_compact(_map(_flatten(_map(blocks, 'exercises')), 'objectID')))
    if (allIds.length === 0) {
        return []
    }

    let filter = `objectID:"${allIds.join('" OR objectID:"')}"`
    // filter += ' AND hide!=1' // No hide old exercises in currents routines

    try {
        const response = await ExerciseIndex.search('', {
            filters: filter,
            hitsPerPage: 10000,
        })
        return response.hits
    } catch (e) {
        console.log('Error has ocurred:', e.message)
        return []
    }

}


export const getMergedBlocks = async (blocks) => {

    const exercises = await getExercisesByBlocks(blocks)
    let newBlocks = []

    blocks.forEach(block => {
        let newBlock = { ...block }
        newBlock.uid = block.uid || randomString(10, '', 'bl')
        let newExercises = []
        block.exercises.forEach(exercise => {
            let foundExercise = exercises.find(ex => exercise.objectID === ex.objectID)
            let exerciseToAdd = { ...exercise, uid: exercise.uid || randomString(10, '', 'ex') }
            if (foundExercise) exerciseToAdd = _merge(exerciseToAdd, foundExercise)
            newExercises.push(exerciseToAdd)
        })
        newBlock.exercises = newExercises
        newBlocks.push(newBlock)
    })
    return newBlocks
}

export const sumDuration = (exercises) => {

    let seconds = i18n.tc('common.seconds', 2)
    let minutes = i18n.tc('common.minutes', 2)
    let hours = i18n.tc('common.hours', 2)

    let total = 0

    for (let i in exercises) {

        let ex = exercises[i]
        let multiplier = 1

        if (!ex.unit) {
            ex.unit = seconds
        }

        if (ex.unit !== seconds) {
            multiplier = ex.unit === minutes ? 60 : 60 * 60
        }

        let duration = (ex.duration && Number(ex.duration) || 0) * multiplier
        total = total + duration

    }

    if (total >= 60 && total <= 3600) {
        return {
            duration: (total / 60).toFixed(2),
            unit: minutes
        }
    }

    if (total > 3600) {
        return {
            duration: (total / 60 / 60).toFixed(2),
            unit: hours
        }
    }

    return {
        duration: total.toFixed(2),
        unit: seconds
    }

}

export const addAssignedRoutinesGroup = async (assignedRoutine, studentsIds) => {
    let payload = getPayloadBaseAssignedRoutine(assignedRoutine.name, assignedRoutine.date, assignedRoutine, assignedRoutine.blocks)

    console.log('addAssignedRoutinesGroup payload', payload)

    let assignedRoutinesIds = assignedRoutine.assignedRoutinesIds || []

    for (let id of studentsIds || []) {
        payload.studentId = id
        const newAssignedRoutine = await db.collection('assignedRoutines').add(payload)
        assignedRoutinesIds.push(newAssignedRoutine.id)
    }

    assignedRoutinesIds = [...new Set(assignedRoutinesIds)]

    await db.collection('groupAssignedRoutines').doc(assignedRoutine.id).update({ assignedRoutinesIds: assignedRoutinesIds })
}

export const updateAssignedRoutinesGroup = async (assignedRoutinesIds, blocks) => {
    for (let id of assignedRoutinesIds || []) {
        await db.collection('assignedRoutines').doc(id).update({ blocks: blocks })
    }
}

export const deleteAssignedRoutinesGroup = async (assignedRoutinesIds) => {
    for (let id of assignedRoutinesIds || []) {
        await db.collection('assignedRoutines').doc(id).delete().catch(error => {
            console.log('error', id)
        })
    }
}

export const hasAssignedRoutineInDate = async (studentId, date) => {
    let assignedRoutinesRef = await db.collection('assignedRoutines').where('studentId', '==', studentId).where('rawDate.full', '==', date).get()
    return { has: assignedRoutinesRef.docs.length > 0, id: assignedRoutinesRef.docs?.[0]?.id || null }
}

export const hasGroupAssignedRoutineInDate = async (date, groupId, teamId = null) => {
    let groupRoutinesRef = await db.collection('groupAssignedRoutines')
        .where('teamId', '==', teamId)
        .where('groupId', '==', groupId)
        .where('date', '==', date).get()
    let payload = { has: groupRoutinesRef.docs.length > 0 }
    if (payload.has) {
        payload.id = groupRoutinesRef.docs[0].id
        payload.assignedRoutinesIds = groupRoutinesRef.docs[0].data().assignedRoutinesIds
    }
    return payload
}

export const getGroupAssignedRoutines = async (groupId, teamId = null) => {
    let now = moment().format('YYYY-MM-DD')
    let groupRoutinesRef = await db.collection('groupAssignedRoutines')
        .where('teamId', '==', teamId)
        .where('groupId', '==', groupId)
        .where('rawDate.full', '>=', now).get()
    return groupRoutinesRef.docs.map(routine => {
        return {
            id: routine.id,
            ...routine.data()
        }
    })
}

export const getAllGroupAssignedRoutines = async (groupId, teamId = null) => {
    let groupRoutinesRef = await db.collection('groupAssignedRoutines')
        .where('teamId', '==', teamId)
        .where('groupId', '==', groupId).get()
    return groupRoutinesRef.docs.map(routine => {
        return {
            id: routine.id,
            ...routine.data()
        }
    })
}

export const getAllStudentAssignedRoutines = async (studentId) => {
    let studentRoutinesRef = await db.collection('assignedRoutines')
        .where('studentId', '==', studentId).get()
    return studentRoutinesRef.docs.map(routine => {
        return {
            id: routine.id,
            ...routine.data()
        }
    })
}

export const deleteAssignedRoutineStudentInDate = async (studentId, date) => {
    let assignedRoutinesRef = await db.collection('assignedRoutines').where('studentId', '==', studentId).where('rawDate.full', '==', date).get()
    for (let routine of assignedRoutinesRef.docs) if (routine?.id) await db.collection('assignedRoutines').doc(routine.id).delete()
}

export const deleteAssignedRoutinesGroupCollection = async (groupId, teamId = null) => {
    let groupAssignedRoutinesRef = await db.collection('groupAssignedRoutines')
        .where('groupId', '==', groupId)
        .where('teamId', '==', teamId).get()
    for (let routine of groupAssignedRoutinesRef.docs) {
        if (routine?.id) await db.collection('groupAssignedRoutines').doc(routine.id).delete()
    }
}

export const removeStudentsFromGroup = async (studentsIds) => {
    for (let id of studentsIds || []) {
        await db.collection('users').doc(id).update({
            group: null,
            team: null,
        })
    }
}

export const deleteGroupTeam = async (id, collection) => {
    await db.collection(collection).doc(id).delete()
}

export const clearBlocksAssignedRoutine = (blocks, reset = true) => {
    if (!blocks) return []
    return blockUniqueTime([...blocks]).map((block, i) => ({
        id: i,
        completed: !reset && block.completed || false,
        duration: block.duration || 0,
        unit: block.unit || i18n.tc('common.minutes', 2),
        sets: block.sets || null,
        tabata: block.tabata || null,
        isCircuit: block.isCircuit || false,
        type: block.type || null,
        exercises: block.exercises.map(exercise => getPayloadExercise(exercise, reset))
    }))
}

export const clearBlock = (block, reset = true) => {
    if (!block) return null
    return {
        completed: !reset && block.completed || false,
        duration: block.duration || 0,
        unit: block.unit || i18n.tc('common.minutes', 2),
        sets: block.sets || null,
        tabata: block.tabata || null,
        isCircuit: block.isCircuit || false,
        type: block.type || null,
        exercises: block.exercises.map(exercise => getPayloadExercise(exercise, reset))
    }
}

export const getPayloadBlock = (name, coachId, block, folder, tags = [], img = null, reset = true) => {
    return {
        name: name,
        block: clearBlock(block, reset),
        coachId: coachId,
        folder: folder.id,
        folderName: folder.name,
        tags: tags,
        img: img?.tempUrl || '',
        imgSavePath: img?.savePath || '',
        created_at: Timestamp.now(),
    }
}

export const getPayloadExercise = (exercise, reset = true) => {
    return {
        completed: !reset && exercise.completed || false,
        type: exercise.type || 'exercise',
        time: exercise.time || '',
        name: exercise.name || null,
        maxWeight: exercise.maxWeight || null,
        eachSide: exercise.eachSide || false,
        commentary: exercise.commentary || '',
        hasTime: !!exercise.time || !!exercise.hasTime,
        id: exercise.id || exercise.objectID,
        config: exercise.config || null,
        objectID: exercise.id || exercise.objectID,
        history: !reset && exercise.history || [],
        painScale: exercise.painScale || false
    }
}

export const blockUniqueTime = (blocks) => {
    if (!blocks) return []
    let newBlocks = []
    blocks.forEach(block => {
        let exercises = []
        block.exercises.forEach((exercise, i) => {
            if (exercise.type === 'exercise') {
                let nextExercise = block.exercises[i + 1]
                if (nextExercise?.type === 'rest') {
                    exercise.time = nextExercise.time
                }
                exercises.push(exercise)
            }
        })

        block.exercises = exercises
        newBlocks.push(block)
    });
    return newBlocks
}

export const hasRoutinesInDates = async (dates, studentId) => {
    let datesSorted = dates.sort()
    let start = datesSorted[0]
    let end = datesSorted[datesSorted.length - 1]

    let assignedRoutinesRef = await db.collection('assignedRoutines')
        .where('studentId', '==', studentId)
        .where('rawDate.full', ">=", start)
        .where('rawDate.full', "<=", end)
        .get()

    return assignedRoutinesRef.docs.map(ar => ({ id: ar.id, ...ar.data() })).filter(r => dates.includes(r.rawDate.full))
}

export const getPayloadBaseAssignedRoutine = (name, date, routine, blocks) => {
    const [year, month, day] = date.split('-');
    return {
        blocks: blocks,
        name: name,
        routineId: routine.routineId || null,
        date: Timestamp.fromMillis(moment(date).unix() * 1000),
        rawDate: {
            full: date,
            day: day,
            month: month,
            year: year
        },
        coachId: routine.coachId,
        img: routine.img || '',
        imgSavePath: routine.imgSavePath || '',
        description: routine.description || null,
        owner: routine.owner || 'user',
    }
}

export const getPayloadBaseRoutine = (name, routine, blocks, category, img, tags = []) => {
    return {
        blocks: blocks,
        coachId: routine.coachId,
        created_at: Timestamp.now(),
        name: name,
        description: {
            blocks: blocks.length,
            equipments: routine?.description?.equipments || [],
            objective: category
        },
        img: img?.tempUrl || '',
        imgSavePath: img?.savePath || '',
        tags: tags,
        owner: routine.owner || 'user',
    }
}

export const getPayloadBaseProgram = (name, coachId, assignedRoutines, category, description, img, tags = []) => {
    return {
        coachId: coachId,
        created_at: Timestamp.now(),
        name: name,
        category: category,
        img: img?.tempUrl || '',
        imgSavePath: img?.savePath || '',
        assignedRoutines: assignedRoutines,
        description: description,
        tags: tags,
        owner: 'user'
    }
}

export const getPayloadBaseAssignedCompetence = (versus, date, coachId) => {
    const [year, month, day] = date.split('-');
    return {
        versus: versus,
        date: Timestamp.fromMillis(moment(date).unix() * 1000),
        rawDate: {
            full: date,
            day: day,
            month: month,
            year: year
        },
        coachId: coachId,
        owner: 'user',
        type: 'competence'
    }
}

export const addAssignedCompetenceGroup = async (competence, studentsIds) => {
    let payload = getPayloadBaseAssignedCompetence(competence.name, competence.date, competence.coachId)

    let assignedRoutinesIds = competence.assignedRoutinesIds || []

    for (let id of studentsIds || []) {
        payload.studentId = id
        const newCompetence = await db.collection('assignedRoutines').add(payload)
        assignedRoutinesIds.push(newCompetence.id)
    }

    assignedRoutinesIds = [...new Set(assignedRoutinesIds)]

    await db.collection('groupAssignedRoutines').doc(competence.id).update({ assignedRoutinesIds: assignedRoutinesIds })
}

export const updateAssignedCompetenceGroup = async (assignedRoutinesIds, versus) => {
    for (let id of assignedRoutinesIds || []) {
        await db.collection('assignedRoutines').doc(id).update({ versus: versus })
    }
}